import React from 'react';
import { useTutorialSafe } from '@/app/contexts/TutorialContext';
import { getTutorialModules } from '@/app/utils/tutorialStepsSimplified';
import { Button } from '../ui/button';
import { X, Play, Clock, CheckCircle } from 'lucide-react';
import type { TutorialModule } from '@/app/types/tutorial';

interface TutorialWelcomeProps {
  onClose: () => void;
}

export function TutorialWelcome({ onClose }: TutorialWelcomeProps) {
  const tutorial = useTutorialSafe();
  const modules = getTutorialModules();

  // If tutorial context not available, show a simplified version
  if (!tutorial) {
    return (
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
        onClick={onClose}
      >
        <div
          className="w-full max-w-3xl rounded-lg shadow-2xl overflow-hidden m-4"
          style={{
            backgroundColor: 'var(--md-surface)',
            color: 'var(--md-on-surface)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Tutorial wird geladen...</h2>
            <p>Bitte warten Sie einen Moment.</p>
          </div>
        </div>
      </div>
    );
  }

  const { startTutorial, setNeverShowAgain, state } = tutorial;

  const handleStartTutorial = (moduleId: string) => {
    startTutorial(moduleId as TutorialModule);
    onClose();
  };

  const handleNeverShowAgain = () => {
    setNeverShowAgain(true);
    onClose();
  };

  const isModuleComplete = (moduleId: string) => {
    return state.progress.completedModules.includes(moduleId);
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl rounded-lg shadow-2xl overflow-hidden m-4"
        style={{
          backgroundColor: 'var(--md-surface)',
          color: 'var(--md-on-surface)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="px-8 py-6 border-b"
          style={{
            backgroundColor: 'var(--md-primary)',
            color: 'var(--md-on-primary)',
            borderColor: 'var(--md-outline-variant)',
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2">Willkommen bei ISOCAD! 👋</h2>
              <p className="text-sm opacity-90">
                Interaktives Tutorial - Lernen Sie ISOCAD in 3 einfachen Schritten
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/20 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Workflow Overview */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4">Der ISOCAD Workflow:</h3>
            <div className="grid grid-cols-3 gap-4">
              <div
                className="p-4 rounded-lg text-center"
                style={{ backgroundColor: 'var(--md-surface-container)' }}
              >
                <div
                  className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center text-xl font-bold"
                  style={{
                    backgroundColor: 'var(--md-primary)',
                    color: 'var(--md-on-primary)',
                  }}
                >
                  1
                </div>
                <div className="font-medium mb-1">ISO.BLOCK</div>
                <div className="text-sm opacity-70">Bauteile konstruieren</div>
              </div>

              <div
                className="p-4 rounded-lg text-center"
                style={{ backgroundColor: 'var(--md-surface-container)' }}
              >
                <div
                  className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center text-xl font-bold"
                  style={{
                    backgroundColor: 'var(--md-secondary)',
                    color: 'var(--md-on-secondary)',
                  }}
                >
                  2
                </div>
                <div className="font-medium mb-1">BLOCK.SYSTEM</div>
                <div className="text-sm opacity-70">Systeme bauen & drucken</div>
              </div>

              <div
                className="p-4 rounded-lg text-center"
                style={{ backgroundColor: 'var(--md-surface-container)' }}
              >
                <div
                  className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center text-xl font-bold"
                  style={{
                    backgroundColor: 'var(--md-tertiary)',
                    color: 'var(--md-on-tertiary)',
                  }}
                >
                  3
                </div>
                <div className="font-medium mb-1">SYSTEM.TABLE</div>
                <div className="text-sm opacity-70">Stücklisten erstellen</div>
              </div>
            </div>
          </div>

          {/* Tutorial Modules */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Wählen Sie ein Tutorial:</h3>

            {modules.map((module) => (
              <div
                key={module.id}
                className="p-6 rounded-lg border flex items-center justify-between hover:shadow-md transition-shadow"
                style={{
                  backgroundColor: 'var(--md-surface-container-low)',
                  borderColor: 'var(--md-outline-variant)',
                }}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="font-semibold text-lg">{module.name}</h4>
                    {isModuleComplete(module.id) && (
                      <CheckCircle
                        className="w-5 h-5"
                        style={{ color: 'var(--md-primary)' }}
                      />
                    )}
                  </div>
                  <p className="text-sm mb-3 opacity-80">{module.description}</p>
                  <div className="flex items-center gap-2 text-sm opacity-70">
                    <Clock className="w-4 h-4" />
                    <span>{module.estimatedTime}</span>
                  </div>
                </div>

                <Button
                  onClick={() => handleStartTutorial(module.id)}
                  className="gap-2 ml-4"
                  style={{
                    backgroundColor: 'var(--md-primary)',
                    color: 'var(--md-on-primary)',
                  }}
                >
                  <Play className="w-4 h-4" />
                  {isModuleComplete(module.id) ? 'Wiederholen' : 'Starten'}
                </Button>
              </div>
            ))}
          </div>

          {/* Info Box */}
          <div
            className="mt-6 p-4 rounded-lg text-sm"
            style={{
              backgroundColor: 'var(--md-secondary-container)',
              color: 'var(--md-on-secondary-container)',
            }}
          >
            <p className="font-medium mb-2">💡 Tipp:</p>
            <p>
              Sie können das Tutorial jederzeit über das Hilfe-Menü (❓) in der oberen Toolbar neu starten.
              Alle Tutorials sind interaktiv - Sie führen echte Aktionen aus und lernen dabei die Software kennen.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div
          className="px-8 py-4 border-t flex items-center justify-between"
          style={{ borderColor: 'var(--md-outline-variant)' }}
        >
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              onChange={(e) => {
                if (e.target.checked) {
                  handleNeverShowAgain();
                }
              }}
              className="rounded"
            />
            <span>Dieses Fenster beim Start nicht mehr anzeigen</span>
          </label>

          <Button variant="outline" onClick={onClose}>
            Überspringen
          </Button>
        </div>
      </div>
    </div>
  );
}